import Component from '@bythepixel/component-loader';

const DEFAULTS = {
  childSelector: '[data-tab-button]',
  activeClass: 'tab--active',
  // Initialize with this index selected
  initSelect: 0,
  // Use query params in the URL to specify state
  queryParamState: false,
  // Callback to be called when a tab is selected.
  // Returns the selected tab
  onSelect: () => {},
  // Callback to be called when a tab is deselected.
  // Returns the deselected tab
  onDeselect: () => {},
};

export default class Tabs extends Component {
  static selector = '[data-comp-tabs]';

  constructor(containerEl, options = DEFAULTS) {
    super(containerEl);

    Object.assign(this.$options, DEFAULTS, options);
    this.allTabs = this.$options.childSelector
      ? [...this.$container.querySelectorAll(this.$options.childSelector)]
      : [...this.$container.children];
    this.activeTab = undefined;
    this.inactiveTabs = [];
    this.clickability = true;
    this.childSelectorAttr = this.$options.childSelector.replace(/[[\]']+/g, '');

    // Activate the first tab
    this.initFirstTab();
    // Wire up click events
    this.events();
  }

  initFirstTab() {
    let tabQueryParams = null;
    let activeTabEl = null;

    if (this.$options.queryParamState) {
      const queryParams = new URLSearchParams(window.location.search);
      tabQueryParams = queryParams.get('tab');
      activeTabEl = tabQueryParams
        ? this.$container.querySelector(`[${this.childSelectorAttr}="${tabQueryParams}"]`)
        : null;
    }

    if (activeTabEl) {
      this.activateTab(activeTabEl);
    } else if (typeof this.$options.initSelect === 'number') {
      this.activateTab(this.allTabs[this.$options.initSelect]);
    }
  }

  events() {
    this.allTabs.forEach(tab => {
      tab.addEventListener('click', this.preventScrollOnClick.bind(this));
      tab.addEventListener('click', this.eventHandler.bind(this));
      tab.addEventListener('keydown', this.eventHandler.bind(this));
    });
  }

  eventHandler(e) {
    if (this.clickability === true) {
      this.activateTab(e.target.closest(this.$options.childSelector));
      this.setSearchParams(e.target);
    }
    if ([32, 13].includes(e.which)) {
      this.activateTab(e.target.closest(this.$options.childSelector));
      this.setSearchParams(e.target);
    }
  }

  /**
   * Activates a tab. This involves:
   * - Setting activeClass on the active tab
   * - Calls onSelect function
   * - Deselects all other tabs
   * @param  {number||HTMLElement} clickedTab The activated tab.
   *                                          If this is a number, treat it as an index.
   *                                          Otherwise, assume it is the HTMLElement
   * @return {void}
   */
  activateTab(clickedTab) {
    if (typeof clickedTab === 'number') {
      // eslint-disable-next-line no-param-reassign
      clickedTab = this.allTabs[clickedTab];
    }
    this.activeTab = clickedTab;
    this.inactiveTabs = this.allTabs.filter(tab => tab !== this.activeTab);

    clickedTab.classList.add(this.$options.activeClass);
    // onSelect callback
    this.$options.onSelect(this.activeTab, this.inactiveTabs);
    // Add active aria attributes
    this.activeTab.setAttribute('aria-selected', 'true');
    this.activeTab.setAttribute('tabindex', '0');
    // Remove all other active classes
    this.inactiveTabs.forEach(tab => this.deactivateTab(tab));
  }

  setSearchParams(tab) {
    if (this.$options.queryParamState) {
      const queryParams = new URLSearchParams(window.location.search);

      queryParams.set('tab', tab.closest(this.$options.childSelector).getAttribute(this.childSelectorAttr));

      window.history.pushState(null, null, `?${queryParams.toString()}`);
    }
  }

  deactivateTab(deselectedTab) {
    // onDeselect callback
    this.$options.onDeselect(deselectedTab);
    // Remove active class
    deselectedTab.classList.remove(this.$options.activeClass);
    // Remove active aria attributes
    deselectedTab.setAttribute('aria-selected', 'false');
    deselectedTab.setAttribute('tabindex', '-1');
  }

  deactivateAllTabs() {
    this.allTabs.forEach(tab => this.deactivateTab(tab));
  }

  // eslint-disable-next-line class-methods-use-this
  preventScrollOnClick(e) {
    if (!e.target.href) return;
    e.preventDefault();
    // Disabled the faqItem Ids while the has is set
    e.target.parentNode.id = `${e.target.parentNode.id}__disabled`;
    // "Navigate"
    // eslint-disable-next-line prefer-destructuring
    window.location.hash = e.target.href.split('#')[1];
    // Restore the Id
    e.target.parentNode.id = e.target.parentNode.id.replace('__disabled', '');
  }

  /*
   * Additional Functionality
   */
  enableClick() {
    this.clickability = true;
  }

  disableClick() {
    this.clickability = false;
  }
}
